import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PricingSection from '../../components/Home/PricingSection';

const CANONICAL = 'https://explanatum.co.uk/modules/seo-content-hub';

const SEOContentHubLanding: React.FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const title = 'SEO Content Hub - Complete Content Workflow | Explanatum';
    const desc = 'Complete SEO workflow in one place: Generate topical authority maps, create optimized blog posts with keyword control, and manage your content library. Professional SEO content without agencies.';

    document.title = title;

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    const setOg = (property: string, content: string) => {
      let el = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('property', property);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', desc);
    setOg('og:title', title);
    setOg('og:description', desc);
    setOg('og:url', CANONICAL);

    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', CANONICAL);
  }, []);

  const track = (event: string, params: Record<string, unknown> = {}) => {
    try {
      const w = window as unknown as { gtag?: (...args: unknown[]) => void };
      if (w && typeof w.gtag === 'function') w.gtag('event', event, params);
    } catch {}
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'What is a topical authority map?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'A topical authority map is a strategic content plan that shows all the topics and subtopics you need to cover to become an authority in your niche. It helps search engines understand your expertise and improves your rankings across multiple keywords.',
        },
      },
      {
        '@type': 'Question',
        name: 'How does the blog creator work?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Our AI Blog Creator generates SEO-optimized articles based on your target keywords. You control keyword density, content length, tone, and structure. Each post includes proper headings, meta descriptions, and internal linking suggestions.',
        },
      },
      {
        '@type': 'Question',
        name: 'Can I manage multiple websites?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes! The Content Library allows you to organize content for multiple websites, track publishing status, and manage your entire content pipeline from one dashboard.',
        },
      },
      {
        '@type': 'Question',
        name: 'How much does it cost?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'The SEO Content Hub is included with your Explanatum subscription. No per-article fees, no word count limits. Create unlimited content for £15.20/month instead of paying agencies £100-500 per article.',
        },
      },
    ],
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 py-20">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSgwLDE1MCwxMDAsMC4wNSkiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-30"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-sm mb-6">
              <span className="text-3xl">📝</span>
              <span className="text-sm font-semibold text-gray-700">Complete SEO Content Workflow</span>
            </div>
            
            <h1 className="text-5xl md:text-6xl font-extrabold text-gray-900 mb-6 leading-tight">
              Create SEO Content That
              <span className="block bg-gradient-to-r from-green-600 via-emerald-600 to-teal-600 bg-clip-text text-transparent">
                Actually Ranks
              </span>
            </h1>
            
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto leading-relaxed">
              Generate topical authority maps, create optimized blog posts with keyword control, and manage your content library—all in one place.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <button
                onClick={() => {
                  track('cta_click', { location: 'hero', tool: 'seo_content_hub' });
                  navigate('/marketing/seo-content-hub');
                }}
                className="px-8 py-4 bg-gradient-to-r from-green-600 to-teal-600 text-white font-bold rounded-xl hover:from-green-700 hover:to-teal-700 transition shadow-lg hover:shadow-xl text-lg"
              >
                Start Creating Content →
              </button>
              <button
                onClick={() => {
                  const el = document.getElementById('features');
                  el?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="px-8 py-4 bg-white text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition shadow-md text-lg"
              >
                See What's Included
              </button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-8 max-w-2xl mx-auto">
              <div className="text-center">
                <div className="text-3xl font-bold text-gray-900">3</div>
                <div className="text-sm text-gray-600">Powerful Tools</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-gray-900">Unlimited</div>
                <div className="text-sm text-gray-600">Content Creation</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-gray-900">£500+</div>
                <div className="text-sm text-gray-600">Saved Per Article</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tools Section */}
      <section id="features" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">3 Essential SEO Content Tools</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Everything you need for professional SEO content creation
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* Topical Authority */}
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-8 border border-green-100 hover:shadow-xl transition">
              <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Topical Authority Maps</h3>
              <p className="text-gray-600 mb-4">Generate comprehensive content strategies showing every topic needed to dominate your niche. Build authority systematically.</p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start gap-2">
                  <span className="text-green-500 mt-0.5">✓</span>
                  <span>AI-generated topic clusters</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-500 mt-0.5">✓</span>
                  <span>Keyword research integration</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-500 mt-0.5">✓</span>
                  <span>Content gap analysis</span>
                </li>
              </ul>
            </div>

            {/* Blog Creator */}
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-8 border border-emerald-100 hover:shadow-xl transition">
              <div className="w-12 h-12 bg-emerald-500 rounded-xl flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">AI Blog Creator</h3>
              <p className="text-gray-600 mb-4">Create SEO-optimized articles with full control over keywords, length, tone, and structure. Professional content in minutes.</p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500 mt-0.5">✓</span>
                  <span>Keyword density control</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500 mt-0.5">✓</span>
                  <span>Custom content length</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500 mt-0.5">✓</span>
                  <span>Internal linking suggestions</span>
                </li>
              </ul>
            </div>

            {/* Content Library */}
            <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-2xl p-8 border border-teal-100 hover:shadow-xl transition">
              <div className="w-12 h-12 bg-teal-500 rounded-xl flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Content Library</h3>
              <p className="text-gray-600 mb-4">Organize and manage all your content in one place. Track publishing status, monitor performance, and streamline your workflow.</p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start gap-2">
                  <span className="text-teal-500 mt-0.5">✓</span>
                  <span>Multi-site management</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-teal-500 mt-0.5">✓</span>
                  <span>Status tracking</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-teal-500 mt-0.5">✓</span>
                  <span>Version history</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Workflow Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Your SEO Content Workflow</h2>
            <p className="text-xl text-gray-600">Strategic, optimized content in 3 simple steps</p>
          </div>

          <div className="max-w-4xl mx-auto space-y-6">
            {[
              { step: 1, title: 'Build Your Strategy', desc: 'Generate topical authority map → identify content gaps → plan your calendar', icon: '🗺️' },
              { step: 2, title: 'Create Content', desc: 'AI Blog Creator → set keywords & tone → generate optimized articles → edit & refine', icon: '✍️' },
              { step: 3, title: 'Manage & Publish', desc: 'Content Library → organize by site → track status → publish & monitor performance', icon: '📚' },
            ].map((item) => (
              <div key={item.step} className="flex gap-6 items-start bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition">
                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-green-500 to-teal-500 rounded-xl flex items-center justify-center text-white font-bold text-lg">
                  {item.step}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-3xl">{item.icon}</span>
                    <h3 className="text-xl font-bold text-gray-900">{item.title}</h3>
                  </div>
                  <p className="text-gray-600">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
          </div>

          <div className="max-w-3xl mx-auto space-y-6">
            {faqSchema.mainEntity.map((faq, idx) => (
              <details key={idx} className="group bg-gray-50 rounded-xl p-6 hover:bg-gray-100 transition">
                <summary className="font-bold text-lg text-gray-900 cursor-pointer list-none flex justify-between items-center">
                  {faq.name}
                  <span className="text-green-600 group-open:rotate-180 transition-transform">▼</span>
                </summary>
                <p className="mt-4 text-gray-600 leading-relaxed">{faq.acceptedAnswer.text}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <PricingSection />

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-green-600 via-emerald-600 to-teal-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Dominate Search Rankings?
          </h2>
          <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">
            Create unlimited SEO-optimized content for less than the cost of one agency article.
          </p>
          <button
            onClick={() => {
              track('cta_click', { location: 'footer', tool: 'seo_content_hub' });
              navigate('/marketing/seo-content-hub');
            }}
            className="px-10 py-4 bg-white text-green-600 font-bold rounded-xl hover:bg-gray-100 transition shadow-xl text-lg"
          >
            Start Creating Content →
          </button>
        </div>
      </section>
    </div>
  );
};

export default SEOContentHubLanding;
